import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../components/Layout';
import api from '../api';
import {
  buildCaseReviewWarnings,
  formatCaseChannel,
  formatCasePriority,
  formatCaseStatus,
  formatSupportCodeState,
  getSupportCodeState,
  parseDate,
  readCaseReviewWorkspace,
  SUPPORT_PRIORITY_OPTIONS,
  SUPPORT_STATUS_OPTIONS,
} from '../utils/supportSignals';

function buildFormState(record) {
  const reviewWorkspace = readCaseReviewWorkspace(record);
  return {
    status: record?.status || 'open',
    priority: record?.priority || 'normal',
    summary: record?.summary || '',
    internalNotes: record?.internalNotes || '',
    customerImpact: reviewWorkspace.customerImpact || '',
    nextAction: reviewWorkspace.nextAction || '',
    resolutionSummary: reviewWorkspace.resolutionSummary || '',
    accessAssistanceRequested: Boolean(record?.accessAssistanceRequested),
  };
}

export default function SupportCaseReview() {
  const navigate = useNavigate();
  const { caseId } = useParams();
  const [record, setRecord] = useState(null);
  const [form, setForm] = useState(buildFormState(null));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const syncRecord = useCallback((nextRecord) => {
    setRecord(nextRecord);
    setForm(buildFormState(nextRecord));
  }, []);

  const loadCase = useCallback(async () => {
    if (!caseId) return;
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get(`/admin/platform/support-cases/${caseId}`);
      syncRecord(data.supportCase || null);
    } catch (err) {
      setError(err?.response?.data?.message || 'Could not load this support case.');
    } finally {
      setLoading(false);
    }
  }, [caseId, syncRecord]);

  useEffect(() => {
    loadCase();
  }, [loadCase]);

  const warnings = useMemo(() => buildCaseReviewWarnings(record), [record]);

  const saveReview = async () => {
    if (!record?._id) return;
    setSaving(true);
    setError('');
    setMessage('');
    try {
      const { data } = await api.patch(`/admin/platform/support-cases/${record._id}`, form);
      syncRecord(data.supportCase || null);
      setMessage(data.message || 'Case review saved.');
      window.dispatchEvent(new Event('support-cases:updated'));
    } catch (err) {
      setError(err?.response?.data?.message || 'Could not save this review.');
    } finally {
      setSaving(false);
    }
  };

  const assignToMe = async () => {
    if (!record?._id) return;
    setAssigning(true);
    setError('');
    setMessage('');
    try {
      const { data } = await api.patch(`/admin/platform/support-cases/${record._id}`, {
        assignToMe: true,
      });
      syncRecord(data.supportCase || null);
      setMessage('Case assigned to you.');
      window.dispatchEvent(new Event('support-cases:updated'));
    } catch (err) {
      setError(err?.response?.data?.message || 'Could not assign this case.');
    } finally {
      setAssigning(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <StatePanel message="Loading support case review..." />
      </Layout>
    );
  }

  if (error && !record) {
    return (
      <Layout>
        <StatePanel tone="error" message={error} />
      </Layout>
    );
  }

  if (!record) {
    return (
      <Layout>
        <StatePanel message="This support case could not be found." />
      </Layout>
    );
  }

  const assignedLabel = record.assignedAdminId
    ? [record.assignedAdminId.firstName, record.assignedAdminId.lastName]
        .filter(Boolean)
        .join(' ') || record.assignedAdminId.email
    : 'Nobody yet';
  const supportCodeState = getSupportCodeState(record);

  const metadataPreview = record.metadata ? JSON.stringify(record.metadata, null, 2) : '';

  return (
    <Layout searchPlaceholder="Search support cases, organizations, or systems...">
      <div className="space-y-8">
        <section className="rounded-[2.2rem] border border-[#f5b221]/18 bg-[radial-gradient(circle_at_top_left,rgba(245,178,33,0.22),transparent_36%),linear-gradient(180deg,#111111,#020202)] p-6 shadow-[0_24px_70px_rgba(0,0,0,0.4)] sm:p-8">
          <div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
            <div className="max-w-4xl">
              <button
                type="button"
                onClick={() => navigate('/support')}
                className="rounded-full border border-[#f5b221]/25 px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-[#f7d88a] transition hover:bg-[#f5b221]/10"
              >
                Back to support queue
              </button>
              <p className="mt-5 text-sm font-bold uppercase tracking-[0.28em] text-[#f7d88a]">
                Case Review
              </p>
              <h1 className="mt-3 text-3xl font-black tracking-[-0.03em] text-white sm:text-4xl">
                {record.title || 'Untitled support case'}
              </h1>
              <p className="mt-3 max-w-3xl text-sm leading-7 text-[#cfcfcf]">
                Review the full customer request, update internal notes, and move the case forward without leaving the admin workflow.
              </p>
              <div className="mt-5 flex flex-wrap gap-3 text-xs font-bold uppercase tracking-[0.18em]">
                <StatusPill value={record.status} />
                <PriorityPill value={record.priority} />
                <StatusPill value={record.channel || 'portal'} subtle />
                <span className="rounded-full border border-[#4e4632] bg-[#111111] px-3 py-1 text-[#d2c5ab]">
                  {record.organizationId?.name || 'Unassigned org'}
                </span>
              </div>
            </div>

            <div className="flex flex-wrap gap-3 xl:max-w-md xl:justify-end">
              <button
                type="button"
                onClick={assignToMe}
                disabled={assigning}
                className="rounded-full border border-[#f5b221]/35 px-5 py-3 text-sm font-bold text-[#f7d88a] transition hover:bg-[#f5b221]/10 disabled:opacity-60"
              >
                {assigning ? 'Assigning...' : 'Assign to me'}
              </button>
              <button
                type="button"
                onClick={saveReview}
                disabled={saving}
                className="rounded-full bg-[#f5b221] px-5 py-3 text-sm font-bold text-black transition hover:bg-[#f7d88a] disabled:opacity-70"
              >
                {saving ? 'Saving...' : 'Save adjustments'}
              </button>
            </div>
          </div>
        </section>

        {message ? (
          <div className="rounded-[1.6rem] border border-emerald-500/30 bg-emerald-500/10 px-5 py-4 text-sm text-emerald-100">
            {message}
          </div>
        ) : null}
        {error ? (
          <div className="rounded-[1.6rem] border border-red-500/30 bg-red-500/10 px-5 py-4 text-sm text-red-100">
            {error}
          </div>
        ) : null}

        <section className="grid gap-5 md:grid-cols-2 xl:grid-cols-5">
          <MetricCard
            title="Opened"
            value={formatDateTime(record.openedAt || record.createdAt)}
            body="When the case first entered the queue"
          />
          <MetricCard
            title="Last activity"
            value={formatDateTime(record.lastActivityAt || record.updatedAt)}
            body="Most recent queue movement or note change"
          />
          <MetricCard
            title="Assigned"
            value={assignedLabel}
            body="Current admin owner for this case"
          />
          <MetricCard
            title="Access help"
            value={record.accessAssistanceRequested ? 'Requested' : 'Not requested'}
            body="Portal or support-access assistance flag"
          />
          <MetricCard
            title="Support code"
            value={formatSupportCodeState(supportCodeState)}
            body="Current state of the support-access window"
          />
        </section>

        <section className="rounded-[2rem] border border-[#f5b221]/14 bg-[#070707] p-6 shadow-[0_18px_60px_rgba(0,0,0,0.34)]">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="text-sm font-bold uppercase tracking-[0.24em] text-[#f7d88a]">
                Review Warnings
              </p>
              <h2 className="mt-2 text-2xl font-black tracking-[-0.03em] text-white">
                What still needs attention on this case
              </h2>
            </div>
          </div>

          <div className="mt-6 grid gap-4 xl:grid-cols-3">
            {warnings.length ? (
              warnings.map((warning) => (
                <WarningCard
                  key={warning.id}
                  severity={warning.severity}
                  title={warning.title}
                  description={warning.description}
                />
              ))
            ) : (
              <div className="rounded-[1.6rem] border border-emerald-500/20 bg-emerald-500/10 px-5 py-5 text-sm text-emerald-100 xl:col-span-3">
                No review blockers are showing right now. You can still update notes or close the case from this workspace.
              </div>
            )}
          </div>
        </section>

        <section className="grid gap-6 xl:grid-cols-[1.05fr,0.95fr]">
          <div className="space-y-6">
            <Panel title="Customer and source context">
              <div className="grid gap-4 md:grid-cols-2">
                <InfoField label="Requested by" value={record.requestedBy?.name || record.requestedBy?.email || record.requestedBy?.shimbirrId || 'Unknown'} />
                <InfoField label="Requester email" value={record.requestedBy?.email || 'Not provided'} />
                <InfoField label="Requester phone" value={record.requestedBy?.phone || 'Not provided'} />
                <InfoField label="Requester Shimbirr ID" value={record.requestedBy?.shimbirrId || 'Not provided'} />
                <InfoField label="Organization" value={record.organizationId?.name || 'Not linked'} />
                <InfoField
                  label="System"
                  value={
                    record.systemId?.name
                      ? `${record.systemId.name} (${record.systemId.systemKey || 'system'})`
                      : record.sourceSystemKey || 'Not linked'
                  }
                />
                <InfoField label="Channel" value={formatCaseChannel(record.channel)} />
                <InfoField label="Partner" value={record.sourcePartner || 'Not provided'} />
                <InfoField label="System status" value={record.systemId?.status || 'Unknown'} />
                <InfoField label="License status" value={record.systemId?.licenseStatus || 'Unknown'} />
              </div>

              <div className="mt-6 space-y-4">
                <TextBlock label="Customer summary" value={record.summary || 'No summary captured yet.'} />
                <TextBlock
                  label="Internal notes"
                  value={record.internalNotes || 'No internal review notes yet.'}
                />
              </div>
            </Panel>

            <Panel title="Review strategy">
              <div className="grid gap-4 md:grid-cols-2">
                <InfoField label="Support code status" value={formatSupportCodeState(supportCodeState)} />
                <InfoField label="Support code expiry" value={formatDateTime(record.supportCodeExpiresAt)} />
                <InfoField label="Assigned owner" value={assignedLabel} />
                <InfoField label="Last activity" value={formatDateTime(record.lastActivityAt || record.updatedAt)} />
              </div>

              <div className="mt-6 space-y-4">
                <TextBlock
                  label="Customer impact"
                  value={form.customerImpact || 'No customer impact summary has been written yet.'}
                />
                <TextBlock
                  label="Next action"
                  value={form.nextAction || 'No next action has been captured yet.'}
                />
                <TextBlock
                  label="Resolution summary"
                  value={form.resolutionSummary || 'No resolution summary has been captured yet.'}
                />
              </div>
            </Panel>

            <Panel title="Source metadata">
              {metadataPreview ? (
                <pre className="overflow-x-auto rounded-[1.6rem] border border-[#2f2a1b] bg-black/50 p-5 text-xs leading-6 text-[#d2c5ab]">
                  {metadataPreview}
                </pre>
              ) : (
                <EmptyBlock message="No extra metadata was attached to this case." />
              )}
            </Panel>
          </div>

          <div className="space-y-6">
            <Panel title="Adjustment panel">
              <div className="flex flex-wrap gap-2">
                {SUPPORT_STATUS_OPTIONS.map((status) => (
                  <button
                    key={status}
                    type="button"
                    onClick={() => setForm((current) => ({ ...current, status }))}
                    className={[
                      'rounded-full px-3 py-2 text-xs font-bold uppercase tracking-[0.18em] transition',
                      form.status === status
                        ? 'bg-[#f5b221] text-black'
                        : 'border border-[#4e4632] bg-[#111111] text-[#d2c5ab] hover:bg-[#171717]',
                    ].join(' ')}
                  >
                    {formatCaseStatus(status)}
                  </button>
                ))}
              </div>

              <div className="mt-6 space-y-4">
                <Field label="Status">
                  <select
                    value={form.status}
                    onChange={(event) =>
                      setForm((current) => ({ ...current, status: event.target.value }))
                    }
                    className={inputClass}
                  >
                    {SUPPORT_STATUS_OPTIONS.map((status) => (
                      <option key={status} value={status}>
                        {formatCaseStatus(status)}
                      </option>
                    ))}
                  </select>
                </Field>

                <Field label="Priority">
                  <select
                    value={form.priority}
                    onChange={(event) =>
                      setForm((current) => ({ ...current, priority: event.target.value }))
                    }
                    className={inputClass}
                  >
                    {SUPPORT_PRIORITY_OPTIONS.map((priority) => (
                      <option key={priority} value={priority}>
                        {formatCasePriority(priority)}
                      </option>
                    ))}
                  </select>
                </Field>

                <Field label="Customer-facing summary">
                  <textarea
                    rows={4}
                    value={form.summary}
                    onChange={(event) =>
                      setForm((current) => ({ ...current, summary: event.target.value }))
                    }
                    className={inputClass}
                  />
                </Field>

                <Field label="Internal review notes">
                  <textarea
                    rows={5}
                    value={form.internalNotes}
                    onChange={(event) =>
                      setForm((current) => ({ ...current, internalNotes: event.target.value }))
                    }
                    className={inputClass}
                  />
                </Field>

                <Field label="Customer impact">
                  <textarea
                    rows={3}
                    value={form.customerImpact}
                    onChange={(event) =>
                      setForm((current) => ({ ...current, customerImpact: event.target.value }))
                    }
                    className={inputClass}
                    placeholder="What the customer is currently blocked by"
                  />
                </Field>

                <Field label="Next action">
                  <textarea
                    rows={3}
                    value={form.nextAction}
                    onChange={(event) =>
                      setForm((current) => ({ ...current, nextAction: event.target.value }))
                    }
                    className={inputClass}
                    placeholder="What should happen next after this review"
                  />
                </Field>

                <Field label="Resolution summary">
                  <textarea
                    rows={4}
                    value={form.resolutionSummary}
                    onChange={(event) =>
                      setForm((current) => ({ ...current, resolutionSummary: event.target.value }))
                    }
                    className={inputClass}
                    placeholder="Capture the fix, workaround, or closure reason"
                  />
                </Field>

                <label className="flex items-center justify-between gap-4 rounded-[1.4rem] border border-[#2f2a1b] bg-black/35 px-4 py-4">
                  <div>
                    <p className="text-sm font-bold text-white">Access assistance requested</p>
                    <p className="mt-1 text-sm text-[#b8b8b8]">
                      Keep this on when the customer still needs login, OTP, or support-access help.
                    </p>
                  </div>
                  <input
                    type="checkbox"
                    checked={form.accessAssistanceRequested}
                    onChange={(event) =>
                      setForm((current) => ({
                        ...current,
                        accessAssistanceRequested: event.target.checked,
                      }))
                    }
                    className="h-5 w-5 rounded border-[#4e4632] bg-[#111111] text-[#f5b221] focus:ring-[#f5b221]"
                  />
                </label>
              </div>
            </Panel>

            <Panel title="Review snapshot">
              <div className="grid gap-4">
                <InfoField label="Created at" value={formatDateTime(record.createdAt)} />
                <InfoField label="Updated at" value={formatDateTime(record.updatedAt)} />
                <InfoField label="Resolved at" value={formatDateTime(record.resolvedAt)} />
                <InfoField label="Support code expiry" value={formatDateTime(record.supportCodeExpiresAt)} />
              </div>
            </Panel>
          </div>
        </section>
      </div>
    </Layout>
  );
}

function Panel({ title, children }) {
  return (
    <section className="rounded-[2rem] border border-[#f5b221]/14 bg-[#070707] p-6 shadow-[0_18px_60px_rgba(0,0,0,0.34)]">
      <h2 className="text-2xl font-black tracking-[-0.03em] text-white">{title}</h2>
      <div className="mt-5">{children}</div>
    </section>
  );
}

function MetricCard({ title, value, body }) {
  return (
    <div className="rounded-[1.8rem] border border-[#f5b221]/12 bg-[linear-gradient(180deg,#111111,#040404)] p-5">
      <p className="text-xs font-bold uppercase tracking-[0.22em] text-[#8f8f8f]">{title}</p>
      <p className="mt-3 text-lg font-black tracking-[-0.03em] text-[#f5b221]">{value}</p>
      <p className="mt-2 text-sm text-[#b4b4b4]">{body}</p>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-2 block text-xs font-bold uppercase tracking-[0.22em] text-[#8f8f8f]">
        {label}
      </span>
      {children}
    </label>
  );
}

function InfoField({ label, value }) {
  return (
    <div className="rounded-[1.4rem] border border-[#2f2a1b] bg-black/35 px-4 py-4">
      <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#8f8f8f]">{label}</p>
      <p className="mt-2 text-sm text-white">{value}</p>
    </div>
  );
}

function TextBlock({ label, value }) {
  return (
    <div>
      <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#8f8f8f]">{label}</p>
      <div className="mt-2 rounded-[1.4rem] border border-[#2f2a1b] bg-black/35 px-4 py-4 text-sm leading-7 text-[#d2c5ab]">
        {value}
      </div>
    </div>
  );
}

function WarningCard({ severity, title, description }) {
  const tone =
    severity === 'critical'
      ? 'border-red-500/30 bg-red-500/10 text-red-100'
      : severity === 'warning'
        ? 'border-[#fd6c00]/35 bg-[#fd6c00]/10 text-[#ffcfb0]'
        : 'border-sky-500/30 bg-sky-500/10 text-sky-100';

  return (
    <div className={`rounded-[1.6rem] border px-5 py-5 ${tone}`}>
      <p className="text-xs font-bold uppercase tracking-[0.22em]">{severity}</p>
      <p className="mt-3 text-lg font-bold">{title}</p>
      <p className="mt-2 text-sm leading-7">{description}</p>
    </div>
  );
}

function StatusPill({ value, subtle = false }) {
  const tone = subtle
    ? 'border-[#4e4632] bg-[#111111] text-[#d2c5ab]'
    : 'border-[#f5b221]/20 bg-[#f5b221]/10 text-[#f7d88a]';
  return (
    <span className={`rounded-full border px-3 py-1 ${tone}`}>
      {formatCaseStatus(value)}
    </span>
  );
}

function PriorityPill({ value }) {
  const normalized = String(value || '').toLowerCase();
  const tone =
    normalized === 'critical'
      ? 'border-red-500/30 bg-red-500/10 text-red-100'
      : normalized === 'high'
        ? 'border-[#fd6c00]/35 bg-[#fd6c00]/10 text-[#ffcfb0]'
        : 'border-[#4e4632] bg-[#111111] text-[#d2c5ab]';
  return (
    <span className={`rounded-full border px-3 py-1 ${tone}`}>
      {formatCasePriority(value)}
    </span>
  );
}

function StatePanel({ message, tone = 'default' }) {
  return (
    <div
      className={[
        'flex min-h-[18rem] items-center justify-center rounded-[2rem] border p-8 text-center text-sm shadow-[0_18px_60px_rgba(0,0,0,0.34)]',
        tone === 'error'
          ? 'border-red-500/25 bg-red-950/15 text-red-200'
          : 'border-[#f5b221]/14 bg-[#070707] text-[#d8d8d8]',
      ].join(' ')}
    >
      {message}
    </div>
  );
}

function EmptyBlock({ message }) {
  return (
    <div className="rounded-[1.6rem] border border-dashed border-[#2b2b2b] bg-black/40 p-5 text-sm text-[#989898]">
      {message}
    </div>
  );
}

function formatDateTime(value) {
  const date = parseDate(value);
  return date ? date.toLocaleString() : 'Not available';
}

const inputClass =
  'w-full rounded-[1.3rem] border border-[#3b3525] bg-[#111111] px-4 py-3 text-sm text-white outline-none transition focus:border-[#f5b221]';
